/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ClaimDetailModel} from '../../api/ms-claims/contracts/claim-detail.model';
import {forkJoin, Subscription, timer} from 'rxjs';
import {ClaimPositionDetailModel} from '../../api/ms-claims/contracts/claim-position-detail.model';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {Permission} from '../../shared/utils/permission.enum';
import {UserService} from '../../shared/services/user.service';
import {AvailableActions} from '../../api/ms-claims/contracts/available-actions.enum';
import {ClaimStatusEnum} from '../../api/ms-claims/contracts/claim-status.enum';
import {Location} from '@angular/common';
import {ClaimCommandModel} from '../../api/ms-claims/contracts/claim-command.model';
import {MaterialPositionTableService} from '../shared/material-position-table/service/material-position-table.service';
import {ClaimPositionCommandModel} from '../../api/ms-claims/contracts/claim-position-command.model';
import {ClaimsApi} from '../../api/ms-claims/claims.api';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ClaimInformationDataModel} from '../models/claim-information-data.model';
import {HandlingUnitValidationService} from '../services/claim-detail/handling-unit-validation/handling-unit-validation.service';
import {createProgressFromStatusArray} from '../workflow-configuration/workflow-to-progress-model-mapper';
import {ClaimProgressStepModel} from '../shared/claim-progress/claim-progress-step.model';
import {take} from 'rxjs/operators';
import {ReferenceDataApi} from '../../api/ms-claims/reference-data.api';
import {StepPositionDetailMaterialModel} from '../claim-create-new/step-position-detail/step-position-detail-material.model';
import {ClaimInformationMandatoryFieldsEnum} from '../shared/claim-information-form/claim-information-mandatory-fields.enum';
import {TooltipEnum} from '../../shared/tooltip/tooltip.enum';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {FileManagerApi} from '../../api/ms-filemanager/filemanager.api';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

@Component({
    selector: 'app-claim-edit-page',
    templateUrl: './claim-edit-page.component.html',
    styleUrls: ['./claim-edit-page.component.css', '../../styles/base-edit-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimEditPageComponent implements OnInit, OnDestroy {

    @ViewChild('tooltip') tooltip: TemplateRef<any>;
    public claimDetail: ClaimDetailModel;
    public updatedClaimDetail: ClaimDetailModel & { isAttachmentsTouched: boolean };
    public positions: ClaimPositionDetailModel[] = [];
    public claimCanBeSaved = true;
    public isLoading = true;
    public isExited = false;
    Permission = Permission;
    AvailableActions = AvailableActions;
    public statusSchemaView: ClaimProgressStepModel = null;
    public positionsDependentFieldParentsErrors = new EventEmitter<Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>>();
    public plantCode: string;
    public partnerCode: string;
    public claimCode: string;
    public material: StepPositionDetailMaterialModel;
    public isFileManagerOnline = false;
    public MODAL_TITLE = 'Claim edition';
    public maxNumberOfPositions = 0;
    private informationSectionCanBeSaved = true;
    private positionsSectionCanBeSaved = true;
    private newPositions: ClaimPositionCommandModel[] = [];
    private subscriptions = new Subscription();
    private wasSubmitted = false;

    /*
     * TODO : Create a shared file w/ const values across the application
     */

    constructor(
            private activatedRoute: ActivatedRoute,
            private router: Router,
            private loadingService: LoadingService,
            private userService: UserService,
            private location: Location,
            private positionService: MaterialPositionTableService,
            private claimApi: ClaimsApi,
            private toast: ToastWrapperService,
            private modalService: NgbModal,
            private handlingUnitValidation: HandlingUnitValidationService,
            private referenceDataApi: ReferenceDataApi,
            private fileManagerApi: FileManagerApi,
            private unsavedChangesService: UnsavedChangesService,
            private replay: ReplayableRouterEventsService,
    ) {
    }

    ngOnInit(): void {
        this.getClaimConfig();

        this.subscriptions.add(forkJoin(
                this.referenceDataApi.getStatusOptions(),
                this.activatedRoute.data.pipe(take(1))
        ).subscribe(([options, data]) => {
            this.statusSchemaView = createProgressFromStatusArray(options, data.claimDetail.statusSchema, this.tooltip);
        }));

        this.subscriptions.add(this.activatedRoute.data.subscribe(data => {
            if (data.claimDetail) {
                this.claimDetail = data.claimDetail;

                const initialState = Object.assign({}, this.claimDetail, {isAttachmentsTouched: false});

                this.unsavedChangesService.originalValue = initialState;
                this.updatedClaimDetail = Object.assign({}, this.claimDetail, {isAttachmentsTouched: false});
                this.positions = this.claimDetail.claimPositions;
                // TODO: Remove timer()
                //  Creating dynamically a new view (ex.: toast) from a lifecycle hook (ex.: ngOnInit) will result in
                //  an 'ExpressionChangedAfterItHasBeenCheckedError'. Using timer() is an workaround.
                //  Issue on github: https://github.com/angular/angular/issues/15634
                timer(0).subscribe(() => {
                    this.handlingUnitValidation.handlingUnitValidationErrorsToast(this.claimDetail);
                });

            } else {
                this.router.navigate(['/', 'claim']);
            }
        }, error => {
            this.router.navigate(['/', 'claim']);
        }));
        this.updatePositionsCommandModel();

        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.updateCurrentState();
                }));
    }

    ngOnDestroy(): void {
        if (this.userService.hasPermissions([
            Permission.PARTNER_DIRECTORY_CREATE,
            Permission.PARTNER_FILE_UPLOAD
        ]) && this.isFileManagerOnline) {
            if (this.wasSubmitted) {
                this.fileManagerApi.deleteDirectory(this.claimDetail.attachmentDirectory).subscribe();
            } else {
                this.fileManagerApi.deleteDirectory(this.updatedClaimDetail.attachmentDirectory).subscribe();
            }
        }
        this.subscriptions.unsubscribe();
    }

    cancelClick = (): void => {
        if (this.claimDetail.status.code === ClaimStatusEnum.SUBMITTED) {
            this.router.navigate(['/', 'claim', this.claimDetail.number]);
        } else {
            this.router.navigate(['/', 'claim']);
        }
    };

    checkFileManagerIsOnline = (isOnline: boolean): void => {
        this.isFileManagerOnline = isOnline;
    };

    informationSectionIsValid = (canSave: boolean): void => {
        timer(0).subscribe(() => {
            this.informationSectionCanBeSaved = canSave;
            this.enableSaveButton();
        });
    };

    positionsSectionIsValid = (canSave: boolean): void => {
        timer(0).subscribe(() => {
            this.positionsSectionCanBeSaved = canSave;
            this.enableSaveButton();
        });
    };

    enableSaveButton = (): void => {
        this.claimCanBeSaved = this.informationSectionCanBeSaved && this.positionsSectionCanBeSaved;
    };

    saveClick = (): void => {
        this.wasSubmitted = true;
        this.loadingService.start();
        this.updateClaim();
    };

    updateClaimInformation = (claimInformation: ClaimInformationDataModel): void => {
        this.updatedClaimDetail.attachmentDirectory = claimInformation.attachmentDirectory;
        this.updatedClaimDetail.supplyingPackingPlant = claimInformation.supplyingPackingPlant;
        this.updatedClaimDetail.partner = claimInformation.partner;
        this.updatedClaimDetail.claimCode = claimInformation.claimCode;
        this.updatedClaimDetail.material = claimInformation.material;

        this.updatedClaimDetail.modelType = claimInformation.modelType;
        this.updatedClaimDetail.modelCode = claimInformation.modelCode;
        this.updatedClaimDetail.lotNumber = claimInformation.lotNumber;
        this.updatedClaimDetail.mcdrNumber = claimInformation.mcdrNumber;

        this.updatedClaimDetail.description = claimInformation.description;
        this.updatedClaimDetail.subject = claimInformation.subject;
        this.updatedClaimDetail.spoNumber = claimInformation.spoNumber;

        this.plantCode = this.updatedClaimDetail.supplyingPackingPlant ? this.updatedClaimDetail.supplyingPackingPlant.code : null;
        this.partnerCode = this.updatedClaimDetail.partner ? this.updatedClaimDetail.partner.code : null;
        this.claimCode = this.updatedClaimDetail.claimCode ? this.updatedClaimDetail.claimCode.code : null;
        this.material = this.updatedClaimDetail.material ? this.updatedClaimDetail.material : null;
    };

    updateClaimAttachments = (): void => {
        this.updatedClaimDetail.isAttachmentsTouched = true;
    };
    manageLoading = (isLoading: boolean): void => {
        timer(0).subscribe(() => {
            this.isLoading = isLoading;
        });
    };

    updateClaim = (): void => {
        this.loadingService.start();
        this.subscriptions.add(this.claimApi.updateClaim(this.claimDetail.number,
                this.createUpdatedClaimCommandModel()).subscribe((data) => {
                    this.isExited = true;
                    this.router.navigate(['/', 'claim', data.number]);
                    this.toast.success(`Claim No.<span class="toast-edited-claim-number">${data.number}</span>`
                                     +`<strong> updated</strong> successfully!`, 'Success');
                }, () => {
                    this.errorModal(`Something went wrong while saving Claim's changes,`, 'Error');
                }).add(() => {
                    this.loadingService.stop();
                })
        );
    };

    createUpdatedClaimCommandModel = (): ClaimCommandModel => ({
            partnerCode: null,
            supplyingPackingPlantCode: this.updatedClaimDetail.supplyingPackingPlant.code,
            materialNumber: this.updatedClaimDetail.material.number,
            claimCode: this.updatedClaimDetail.claimCode.code,
        modelCode: this.updatedClaimDetail.modelCode && this.updatedClaimDetail.modelCode.length > 0 ?
                this.updatedClaimDetail.modelCode : null,
        modelType: this.updatedClaimDetail.modelType && this.updatedClaimDetail.modelType.length > 0 ?
                this.updatedClaimDetail.modelType : null,
        lotNumber: this.updatedClaimDetail.lotNumber && this.updatedClaimDetail.lotNumber.length > 0 ?
                this.updatedClaimDetail.lotNumber : null,
        mcdrNumber: this.updatedClaimDetail.mcdrNumber && this.updatedClaimDetail.mcdrNumber.length > 0 ?
                this.updatedClaimDetail.mcdrNumber : null,
        subject: this.updatedClaimDetail.subject && this.updatedClaimDetail.subject.length > 0 ?
                this.updatedClaimDetail.subject : null,
        spoNumber: this.updatedClaimDetail.spoNumber && this.updatedClaimDetail.spoNumber.length > 0 ?
                this.updatedClaimDetail.spoNumber : null,
        description: this.updatedClaimDetail.description && this.updatedClaimDetail.description.length > 0 ?
                toBase64Utf16LittleEndian(this.updatedClaimDetail.description) : null,
        claimPositions: this.newPositions,
        attachmentDirectory: this.updatedClaimDetail.attachmentDirectory
    });

    updatePositionsCommandModel = (): void => {
        this.subscriptions.add(this.positionService.positionData
                .subscribe((positions: ClaimPositionDetailModel[]) => {
                    this.positions = positions;
                    this.newPositions = positions.map(position => ({
                            quantity: position.quantity,
                            decisionCode: position.decision ? position.decision.code : null,
                            claimErrorCode: position.claimError ? position.claimError.code : null,
                            handlingUnit: position.handlingUnit ? position.handlingUnit : null,
                            timeEffort: position.timeEffort,
                            businessCategoryCode: position.businessCategory ? position.businessCategory.code : null
                        }));
                }));
    };

    informationMandatoryFieldsErrorsMap(errorsMap: Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>): void {
        this.positionsDependentFieldParentsErrors.emit(errorsMap);
    }

    enablePositionTableButtons = (): boolean => (this.material != null && this.material.number != null)
                && this.claimCode != null
                && this.plantCode != null
                && this.partnerCode != null;

    headerIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }

    /**
     * Updates the claim current state no manage the discard modal
     */
    private updateCurrentState(): void {
        const currentState = Object.assign({}, this.updatedClaimDetail);
        delete currentState.attachmentDirectory;
        this.unsavedChangesService.currentDelta = currentState;
        const currentPositions = Object.assign([], this.positions);
        currentPositions.map((position: any) => {
            delete position.decisionIsValid;
        });
        this.unsavedChangesService.currentDelta = {claimPositions: currentPositions};
    }

    private errorModal(subtitle: string, title: string, callback?: Function): void {
        const errorModalInstance: ConfirmModalComponent = this.modalService
                .open(ConfirmModalComponent, {centered: true}).componentInstance;
        errorModalInstance.initModal({
            titleText: title,
            subtitleText: subtitle,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            primaryButtonText: null,
            secondaryButtonText: 'Close',
        } as ConfirmModal);
        if (typeof callback === 'function') {
            callback();
        }
    }

    private getClaimConfig(): void {
        this.subscriptions.add(this.claimApi.getClaimConfig().subscribe(config => {
            this.maxNumberOfPositions = config.maxNumberOfPositions;
        }));
    }
}
